import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase-client';

export interface AdminUser {
  id: string;
  email: string;
  display_name: string | null;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  active: boolean;
}

interface AuthContextValue {
  session: Session | null;
  adminUser: AdminUser | null;
  loading: boolean;
  unauthorized: boolean;
  serviceUnavailable: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function syncTokenToSessionStorage(accessToken: string) {
  sessionStorage.setItem('gfs_admin_auth', '1');
  sessionStorage.setItem('gfs_admin_token', accessToken);
}

function clearTokenFromSessionStorage() {
  sessionStorage.removeItem('gfs_admin_auth');
  sessionStorage.removeItem('gfs_admin_token');
}

async function fetchAdminUser(accessToken: string): Promise<AdminUser | 'unauthorized' | 'unavailable' | null> {
  try {
    const res = await fetch('/api/admin/me', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (res.status === 403) return 'unauthorized';
    if (res.status === 503) return 'unavailable';
    if (!res.ok) return null;
    return res.json() as Promise<AdminUser>;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);

  async function handleSession(s: Session | null) {
    setSession(s);
    if (!s) {
      setAdminUser(null);
      setUnauthorized(false);
      setServiceUnavailable(false);
      clearTokenFromSessionStorage();
      return;
    }

    const result = await fetchAdminUser(s.access_token);
    if (result === 'unauthorized') {
      setAdminUser(null);
      setUnauthorized(true);
      setServiceUnavailable(false);
      clearTokenFromSessionStorage();
    } else if (result === 'unavailable') {
      setAdminUser(null);
      setUnauthorized(false);
      setServiceUnavailable(true);
      clearTokenFromSessionStorage();
    } else if (result) {
      setAdminUser(result);
      setUnauthorized(false);
      setServiceUnavailable(false);
      syncTokenToSessionStorage(s.access_token);
    }
  }

  useEffect(() => {
    // Supabase unavailable (env vars not set) — skip auth, unblock the app immediately
    if (!supabase) {
      setLoading(false);
      return;
    }

    const pkceCode = new URLSearchParams(window.location.search).get('code');
    let initialized = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, s) => {
      if (!initialized) return;
      await handleSession(s);
    });

    async function init() {
      if (pkceCode) {
        const { data, error } = await supabase!.auth.exchangeCodeForSession(pkceCode);
        await handleSession(!error && data.session ? data.session : null);
      } else {
        const { data: { session: s } } = await supabase!.auth.getSession();
        await handleSession(s);
      }
      initialized = true;
      setLoading(false);
    }

    const initTimeout = setTimeout(() => { initialized = true; setLoading(false); }, 15000);
    init()
      .catch(() => { initialized = true; setLoading(false); })
      .finally(() => clearTimeout(initTimeout));

    return () => subscription.unsubscribe();
  }, []);

  async function signInWithGoogle() {
    if (!supabase) return;
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/admin/login` },
    });
  }

  async function signOut() {
    clearTokenFromSessionStorage();
    if (supabase) await supabase.auth.signOut();
    setAdminUser(null);
    setSession(null);
    setUnauthorized(false);
    setServiceUnavailable(false);
  }

  return (
    <AuthContext.Provider value={{ session, adminUser, loading, unauthorized, serviceUnavailable, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
